import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import Collapse from '@material-ui/core/Collapse'
import Typography from '@material-ui/core/Typography'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { organizationActions, usergroupActions } from '../../../../_state/actions'
import { FormattedMessage } from 'react-intl'

const useStyles = makeStyles((theme) => ({
    filterRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-end',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    filterControl: {
        minWidth: 200,
    },
    previewPanel: {
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        minHeight: 280,
    },
    userList: {
        maxHeight: '200px',
        overflow: 'auto',
        marginTop: '8px',
    },
    resultIcon: {
        fontSize: 48,
        marginBottom: '8px',
    },
    resultContainer: {
        textAlign: 'center',
        padding: '24px 0',
    },
    composeField: {
        marginBottom: '16px',
    },
    summarySection: {
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
    },
}))

const STEPS = ['Select Recipients', 'Compose Email', 'Confirm & Send', 'Results']

const SendEmailToStudentsDialog = (props) => {
    const classes = useStyles()
    const { open, onClose, dispatch, organization, usergroup } = props

    const [activeStep, setActiveStep] = useState(0)

    const [filterSignedIn, setFilterSignedIn] = useState('all')
    const [filterActive, setFilterActive] = useState('all')
    const [activeDays, setActiveDays] = useState(30)
    const [filterCourseGroup, setFilterCourseGroup] = useState('all')
    const [filterCourseResult, setFilterCourseResult] = useState('all')
    const [filterClassroom, setFilterClassroom] = useState('all')
    const [filterInAnyClassroom, setFilterInAnyClassroom] = useState('all')

    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')

    const [excludedEmails, setExcludedEmails] = useState(new Set())
    const [showConfirmRecipients, setShowConfirmRecipients] = useState(false)

    useEffect(() => {
        if (open) {
            dispatch(organizationActions.getOrganizationCourseGroups(organization.selectedOrganization))
            dispatch(usergroupActions.getAllUserGroupsForOrganisation(organization.selectedOrganization))
            dispatch(organizationActions.resetOrganizationEmail())
            setActiveStep(0)
            setSubject('')
            setBody('')
            setExcludedEmails(new Set())
            setFilterSignedIn('all')
            setFilterActive('all')
            setActiveDays(30)
            setFilterCourseGroup('all')
            setFilterCourseResult('all')
            setFilterClassroom('all')
            setFilterInAnyClassroom('all')
        }
    }, [open])

    useEffect(() => {
        if (!open) return
        const timer = setTimeout(() => {
            const filters = buildFilters()
            dispatch(organizationActions.previewOrganizationEmail(organization.selectedOrganization, filters))
        }, 300)
        return () => clearTimeout(timer)
    }, [filterSignedIn, filterActive, activeDays, filterCourseGroup,
        filterCourseResult, filterClassroom, filterInAnyClassroom, open])

    useEffect(() => {
        setExcludedEmails(new Set())
    }, [organization.emailPreviewResult])

    useEffect(() => {
        if (organization.emailSendResult && !organization.isSendingOrganizationEmail) {
            setActiveStep(3)
        }
    }, [organization.emailSendResult, organization.isSendingOrganizationEmail])

    const buildFilters = () => {
        const filters = {}
        if (filterSignedIn !== 'all') {
            filters.hasSignedIn = filterSignedIn === 'true'
        }
        if (filterActive !== 'all') {
            filters.isActiveLastNDays = filterActive === 'true'
            filters.activeDaysThreshold = activeDays || 30
        }
        if (filterCourseGroup !== 'all' && filterCourseResult !== 'all') {
            filters.courseGroupId = filterCourseGroup
            if (filterCourseResult === 'hasResults') {
                filters.hasCourseResults = true
            } else if (filterCourseResult === 'noResults') {
                filters.hasCourseResults = false
            } else if (filterCourseResult === 'completed') {
                filters.hasCompletedCourse = true
            } else if (filterCourseResult === 'notCompleted') {
                filters.hasCompletedCourse = false
            }
        }
        if (filterClassroom !== 'all') {
            filters.userGroupId = filterClassroom
        } else if (filterInAnyClassroom !== 'all') {
            filters.isInAnyClassroom = filterInAnyClassroom === 'true'
        }
        return filters
    }

    const handleSend = () => {
        const filters = buildFilters()
        const payload = {
            ...filters,
            subject,
            body,
            excludedEmails: excludedEmails.size > 0 ? Array.from(excludedEmails) : undefined,
        }
        dispatch(organizationActions.sendOrganizationEmail(organization.selectedOrganization, payload))
    }

    const handleClose = () => {
        dispatch(organizationActions.resetOrganizationEmail())
        onClose()
    }

    const handleNext = () => {
        setActiveStep((prev) => prev + 1)
    }

    const handleBack = () => {
        setActiveStep((prev) => prev - 1)
    }

    const activeRecipientCount = (organization.emailPreviewResult?.matchingUserCount || 0) - excludedEmails.size
    const canProceedFromFilters = activeRecipientCount > 0
    const canProceedFromCompose = subject.trim().length > 0 && body.trim().length > 0

    const courseGroups = organization.organizationCourseGroups || []
    const classrooms = (usergroup?.usergroups || []).filter((ug) => !ug.deletedAt)

    const previewResult = organization.emailPreviewResult
    const sendResult = organization.emailSendResult

    const renderFiltersStep = () => (
        <>
            <div className={classes.filterRow}>
                <FormControl className={classes.filterControl}>
                    <InputLabel>Signed in</InputLabel>
                    <Select
                        value={filterSignedIn}
                        onChange={(e) => setFilterSignedIn(e.target.value)}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="true">Has signed in</MenuItem>
                        <MenuItem value="false">Has not signed in</MenuItem>
                    </Select>
                </FormControl>

                <FormControl className={classes.filterControl}>
                    <InputLabel>Activity</InputLabel>
                    <Select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                </FormControl>

                {filterActive !== 'all' && (
                    <TextField
                        label="Days"
                        type="number"
                        value={activeDays}
                        onChange={(e) => setActiveDays(parseInt(e.target.value) || 30)}
                        style={{ width: 80 }}
                        inputProps={{ min: 1 }}
                    />
                )}
            </div>

            <div className={classes.filterRow}>
                <FormControl className={classes.filterControl}>
                    <InputLabel>Course</InputLabel>
                    <Select
                        value={filterCourseGroup}
                        onChange={(e) => {
                            setFilterCourseGroup(e.target.value)
                            if (e.target.value === 'all') setFilterCourseResult('all')
                        }}>
                        <MenuItem value="all">All courses</MenuItem>
                        {courseGroups.map((cg) => (
                            <MenuItem key={cg.id} value={cg.id}>
                                {cg.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {filterCourseGroup !== 'all' && (
                    <FormControl className={classes.filterControl}>
                        <InputLabel>Course filter</InputLabel>
                        <Select
                            value={filterCourseResult}
                            onChange={(e) => setFilterCourseResult(e.target.value)}>
                            <MenuItem value="all">Any</MenuItem>
                            <MenuItem value="hasResults">Has results</MenuItem>
                            <MenuItem value="noResults">No results</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="notCompleted">Not completed</MenuItem>
                        </Select>
                    </FormControl>
                )}
            </div>

            <div className={classes.filterRow}>
                <FormControl className={classes.filterControl}>
                    <InputLabel>Classroom</InputLabel>
                    <Select
                        value={filterClassroom}
                        onChange={(e) => {
                            setFilterClassroom(e.target.value)
                            if (e.target.value !== 'all') setFilterInAnyClassroom('all')
                        }}>
                        <MenuItem value="all">All classrooms</MenuItem>
                        {classrooms
                            .sort((a, b) => (a.name > b.name ? 1 : -1))
                            .map((ug) => (
                                <MenuItem key={ug.id} value={ug.id}>
                                    {ug.name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>

                {filterClassroom === 'all' && (
                    <FormControl className={classes.filterControl}>
                        <InputLabel>Classroom membership</InputLabel>
                        <Select
                            value={filterInAnyClassroom}
                            onChange={(e) => setFilterInAnyClassroom(e.target.value)}>
                            <MenuItem value="all">Any</MenuItem>
                            <MenuItem value="true">In at least one classroom</MenuItem>
                            <MenuItem value="false">Not in any classroom</MenuItem>
                        </Select>
                    </FormControl>
                )}
            </div>

            <div className={classes.previewPanel}>
                {organization.isFetchingEmailPreview && !previewResult ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading preview...</Typography>
                    </div>
                ) : previewResult ? (
                    <>
                        <Typography variant="subtitle1">
                            {organization.isFetchingEmailPreview && (
                                <CircularProgress size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                            )}
                            <strong>{activeRecipientCount}</strong> of{' '}
                            {previewResult.matchingUserCount} user
                            {previewResult.matchingUserCount !== 1 ? 's' : ''} selected
                        </Typography>
                        {previewResult.users && previewResult.users.length > 0 && (
                            <TableContainer
                                className={classes.userList}
                                style={{
                                    opacity: organization.isFetchingEmailPreview ? 0.5 : 1,
                                    pointerEvents: organization.isFetchingEmailPreview ? 'none' : 'auto',
                                    transition: 'opacity 0.15s ease',
                                }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={excludedEmails.size === 0}
                                                    indeterminate={
                                                        excludedEmails.size > 0 &&
                                                        excludedEmails.size < previewResult.users.length
                                                    }
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setExcludedEmails(new Set())
                                                        } else {
                                                            setExcludedEmails(
                                                                new Set(previewResult.users.map((u) => u.email))
                                                            )
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Email</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewResult.users.map((user, idx) => {
                                            const isIncluded = !excludedEmails.has(user.email)
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            size="small"
                                                            checked={isIncluded}
                                                            onChange={() => {
                                                                setExcludedEmails((prev) => {
                                                                    const next = new Set(prev)
                                                                    if (isIncluded) {
                                                                        next.add(user.email)
                                                                    } else {
                                                                        next.delete(user.email)
                                                                    }
                                                                    return next
                                                                })
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{user.name || '-'}</TableCell>
                                                    <TableCell>{user.email}</TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                ) : null}
            </div>
        </>
    )

    const renderComposeStep = () => (
        <>
            <Typography variant="body2" style={{ marginBottom: '16px' }}>
                Sending to <strong>{activeRecipientCount}</strong> recipient
                {activeRecipientCount !== 1 ? 's' : ''}
            </Typography>
            <TextField
                className={classes.composeField}
                label="Subject"
                fullWidth
                variant="outlined"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
            />
            <TextField
                className={classes.composeField}
                label="Email body"
                fullWidth
                variant="outlined"
                multiline
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                placeholder="Write the email content here..."
            />
        </>
    )

    const includedUsers = (previewResult?.users || []).filter((u) => !excludedEmails.has(u.email))

    const renderConfirmStep = () => (
        <>
            <div className={classes.summarySection}>
                <Typography variant="subtitle2">Recipients</Typography>
                <Typography variant="body2">
                    {activeRecipientCount} user
                    {activeRecipientCount !== 1 ? 's' : ''}
                    {excludedEmails.size > 0 && (
                        <span style={{ color: '#999' }}>
                            {' '}({excludedEmails.size} excluded)
                        </span>
                    )}
                </Typography>
                {includedUsers.length > 0 && (
                    <>
                        <Button
                            size="small"
                            color="primary"
                            onClick={() => setShowConfirmRecipients((prev) => !prev)}
                            style={{ textTransform: 'none', marginTop: 4, padding: '2px 8px' }}>
                            {showConfirmRecipients ? 'Hide recipients' : 'Show recipients'}
                        </Button>
                        <Collapse in={showConfirmRecipients}>
                            <TableContainer className={classes.userList}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Email</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {includedUsers.map((user, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{user.name || '-'}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>
                    </>
                )}
            </div>
            <div className={classes.summarySection}>
                <Typography variant="subtitle2">Subject</Typography>
                <Typography variant="body2">{subject}</Typography>
            </div>
            <div className={classes.summarySection}>
                <Typography variant="subtitle2">Body</Typography>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                    {body}
                </Typography>
            </div>
        </>
    )

    const renderResultsStep = () => (
        <div className={classes.resultContainer}>
            {sendResult && sendResult.emailsSent > 0 && (
                <>
                    <CheckCircleOutlineIcon className={classes.resultIcon} style={{ color: '#4caf50' }} />
                    <Typography variant="h6" style={{ color: '#4caf50' }}>
                        {sendResult.emailsSent} email{sendResult.emailsSent !== 1 ? 's' : ''} sent successfully
                    </Typography>
                </>
            )}
            {sendResult && sendResult.emailsFailed > 0 && (
                <div style={{ marginTop: '16px' }}>
                    <ErrorOutlineIcon
                        className={classes.resultIcon}
                        style={{ color: '#f44336', fontSize: 32 }}
                    />
                    <Typography variant="body1" style={{ color: '#f44336' }}>
                        {sendResult.emailsFailed} email{sendResult.emailsFailed !== 1 ? 's' : ''} failed
                    </Typography>
                    {sendResult.failedEmails && sendResult.failedEmails.length > 0 && (
                        <TableContainer component={Paper} style={{ marginTop: '8px', maxHeight: '200px' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Error</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sendResult.failedEmails.map((fe, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{fe.email}</TableCell>
                                            <TableCell>{fe.errorReason}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </div>
            )}
            {sendResult && sendResult.totalRecipients === 0 && (
                <Typography variant="body1">No emails were sent.</Typography>
            )}
        </div>
    )

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Send email to students</DialogTitle>
            <DialogContent dividers>
                <Stepper activeStep={activeStep} alternativeLabel style={{ paddingBottom: '24px' }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 0 && renderFiltersStep()}
                {activeStep === 1 && renderComposeStep()}
                {activeStep === 2 && renderConfirmStep()}
                {activeStep === 3 && renderResultsStep()}
            </DialogContent>
            <DialogActions>
                {activeStep === 3 ? (
                    <Button onClick={handleClose} color="primary" style={{ textTransform: 'none' }}>
                        Close
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} style={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        {activeStep > 0 && activeStep < 3 && (
                            <Button onClick={handleBack} style={{ textTransform: 'none' }}>
                                Back
                            </Button>
                        )}
                        {activeStep === 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNext}
                                disabled={!canProceedFromFilters}
                                style={{ textTransform: 'none' }}>
                                Next
                            </Button>
                        )}
                        {activeStep === 1 && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNext}
                                disabled={!canProceedFromCompose}
                                style={{ textTransform: 'none' }}>
                                Next
                            </Button>
                        )}
                        {activeStep === 2 && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSend}
                                disabled={organization.isSendingOrganizationEmail}
                                style={{ textTransform: 'none' }}>
                                {organization.isSendingOrganizationEmail ? (
                                    <CircularProgress size={20} style={{ marginRight: 8, color: '#fff' }} />
                                ) : null}
                                Send emails
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}

function mapStateToProps(state) {
    const { organization, usergroup } = state
    return {
        organization,
        usergroup,
    }
}

export default connect(mapStateToProps)(SendEmailToStudentsDialog)
